import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { Save, Building2, Loader2, Shield, AlertTriangle } from 'lucide-react';

const SettingsPage = () => {
  const [company, setCompany] = useState({
    company_name: '', address: '', postal_code: '', city: '',
    phone: '', mobile1: '', email: '', siret: '', tva_number: '',
  });
  const [regParams, setRegParams] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingCompany, setSavingCompany] = useState(false);
  const [savingReg, setSavingReg] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [compRes, regRes] = await Promise.all([
          api.get('/company-info'),
          api.get('/regulatory-params'),
        ]);
        if (compRes.data) setCompany(prev => ({ ...prev, ...compRes.data }));
        if (regRes.data) setRegParams(regRes.data);
      } catch (e) { /* first time */ }
      setLoading(false);
    })();
  }, []);

  const handleSaveCompany = async () => {
    setSavingCompany(true);
    try {
      const res = await api.put('/company-info', company);
      setCompany(prev => ({ ...prev, ...res.data }));
      toast.success('Informations entreprise enregistrées');
    } catch (e) {
      toast.error('Erreur lors de la sauvegarde');
    }
    setSavingCompany(false);
  };

  const handleSaveReg = async () => {
    setSavingReg(true);
    try {
      const res = await api.put('/regulatory-params', regParams);
      setRegParams(res.data);
      toast.success('Paramètres réglementaires enregistrés');
    } catch (e) {
      toast.error('Erreur lors de la sauvegarde');
    }
    setSavingReg(false);
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" /></div>;

  const Field = ({ label, field, placeholder }) => (
    <div className="space-y-1.5">
      <Label className="text-zinc-400 text-xs">{label}</Label>
      <Input value={company[field] || ''} onChange={(e) => setCompany({ ...company, [field]: e.target.value })}
        className="bg-zinc-950 border-zinc-800 text-white" placeholder={placeholder} data-testid={`settings-${field}`} />
    </div>
  );

  const updateCoeff = (key, val) => {
    setRegParams(prev => ({
      ...prev,
      adr_coefficients: { ...prev.adr_coefficients, [key]: parseFloat(val) || 0 }
    }));
  };

  const updateThreshold = (key, val) => {
    setRegParams(prev => ({
      ...prev,
      thresholds: { ...prev.thresholds, [key]: parseFloat(val) || 0 }
    }));
  };

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <h1 className="text-2xl font-heading font-bold text-white">Paramètres</h1>
        <p className="text-zinc-400 text-sm mt-1">Configuration de l'entreprise et de la réglementation</p>
      </div>

      {/* Company Info */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Building2 className="w-5 h-5 text-amber-400" />
            Informations de l'entreprise
          </CardTitle>
          <p className="text-zinc-500 text-sm">Apparaissent sur les bons de livraison et documents de transport.</p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Field label="Raison sociale" field="company_name" placeholder="Maurin Artifice" />
            <Field label="SIRET" field="siret" placeholder="123 456 789 00001" />
          </div>
          <Field label="Adresse" field="address" placeholder="Zone Industrielle de..." />
          <div className="grid grid-cols-3 gap-4">
            <Field label="Code postal" field="postal_code" placeholder="17000" />
            <Field label="Ville" field="city" placeholder="La Rochelle" />
            <Field label="N° TVA" field="tva_number" placeholder="FR12345678901" />
          </div>
          <div className="grid grid-cols-3 gap-4">
            <Field label="Téléphone" field="phone" placeholder="05 46 00 00 00" />
            <Field label="Mobile" field="mobile1" placeholder="06 12 34 56 78" />
            <Field label="Email" field="email" placeholder="contact@entreprise.fr" />
          </div>
          <div className="flex justify-end pt-4 border-t border-zinc-800">
            <Button onClick={handleSaveCompany} className="bg-amber-500 hover:bg-amber-400 text-black" disabled={savingCompany} data-testid="save-company-info">
              {savingCompany ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Enregistrer
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Regulatory Parameters */}
      {regParams && (
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Shield className="w-5 h-5 text-cyan-400" />
              Paramètres réglementaires ADR / TMD
            </CardTitle>
            <p className="text-zinc-500 text-sm">Coefficients et seuils utilisés pour le calcul de conformité transport.</p>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* ADR Coefficients */}
            <div>
              <h4 className="text-sm font-semibold text-zinc-300 mb-3 flex items-center gap-2">
                Coefficients ADR (1.1.3.6)
              </h4>
              <div className="grid grid-cols-4 gap-3">
                {Object.entries(regParams.adr_coefficients || {}).map(([key, val]) => (
                  <div key={key} className="space-y-1">
                    <Label className="text-zinc-400 text-xs">Division {key}</Label>
                    <Input type="number" value={val} onChange={(e) => updateCoeff(key, e.target.value)}
                      className="bg-zinc-950 border-zinc-800 text-white h-8 text-sm font-mono" data-testid={`coeff-${key}`} />
                  </div>
                ))}
              </div>
              <p className="text-zinc-600 text-xs mt-2">Points = NEC (kg) x Coefficient. Total ≤ seuil → exemption partielle.</p>
            </div>

            {/* Thresholds */}
            <div>
              <h4 className="text-sm font-semibold text-zinc-300 mb-3 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-400" />
                Seuils réglementaires
              </h4>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-1">
                  <Label className="text-zinc-400 text-xs">Seuil 1.1.3.6 (points)</Label>
                  <Input type="number" value={regParams.thresholds?.adr_1136_points || 0}
                    onChange={(e) => updateThreshold('adr_1136_points', e.target.value)}
                    className="bg-zinc-950 border-zinc-800 text-white h-8 text-sm font-mono" data-testid="threshold-1136" />
                  <p className="text-zinc-600 text-[10px]">ADR 1.1.3.6 : exemption si total ≤ ce seuil</p>
                </div>
                <div className="space-y-1">
                  <Label className="text-zinc-400 text-xs">TMD 3.4.2 — NEC globale max (kg)</Label>
                  <Input type="number" value={regParams.thresholds?.tmd_342_global_nec_kg || 0}
                    onChange={(e) => updateThreshold('tmd_342_global_nec_kg', e.target.value)}
                    className="bg-zinc-950 border-zinc-800 text-white h-8 text-sm font-mono" data-testid="threshold-tmd-global" />
                  <p className="text-zinc-600 text-[10px]">Seuil global toutes classes confondues</p>
                </div>
                <div className="space-y-1">
                  <Label className="text-zinc-400 text-xs">TMD 3.4.2 — NEC classes hautes max (kg)</Label>
                  <Input type="number" value={regParams.thresholds?.tmd_342_high_class_nec_kg || 0}
                    onChange={(e) => updateThreshold('tmd_342_high_class_nec_kg', e.target.value)}
                    className="bg-zinc-950 border-zinc-800 text-white h-8 text-sm font-mono" data-testid="threshold-tmd-high" />
                  <p className="text-zinc-600 text-[10px]">Seuil pour 1.1 + 1.2 + 1.3 combinés</p>
                </div>
              </div>
            </div>

            {/* Alert Settings */}
            <div>
              <h4 className="text-sm font-semibold text-zinc-300 mb-3">Alertes</h4>
              <div className="grid grid-cols-2 gap-4 items-end">
                <div className="space-y-1">
                  <Label className="text-zinc-400 text-xs">Seuil d'alerte orange (%)</Label>
                  <Input type="number" value={regParams.alerts?.warning_percent || 80}
                    onChange={(e) => setRegParams(prev => ({ ...prev, alerts: { ...prev.alerts, warning_percent: parseInt(e.target.value) || 80 } }))}
                    className="bg-zinc-950 border-zinc-800 text-white h-8 text-sm font-mono" min="50" max="99" data-testid="alert-percent" />
                  <p className="text-zinc-600 text-[10px]">Jauge passe en orange à ce pourcentage</p>
                </div>
                <div className="flex items-center gap-3 pb-1">
                  <Switch checked={regParams.alerts?.block_on_exceed || false}
                    onCheckedChange={(v) => setRegParams(prev => ({ ...prev, alerts: { ...prev.alerts, block_on_exceed: v } }))}
                    data-testid="block-on-exceed" />
                  <Label className="text-zinc-400 text-xs">Bloquer si dépassement</Label>
                </div>
              </div>
            </div>

            <div className="flex justify-end pt-4 border-t border-zinc-800">
              <Button onClick={handleSaveReg} className="bg-cyan-500 hover:bg-cyan-400 text-black" disabled={savingReg} data-testid="save-regulatory-params">
                {savingReg ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Enregistrer les paramètres
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default SettingsPage;
